import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  NativeSelect,
  Paper,
  Select,
  SelectChangeEvent,
  Skeleton,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import HomeIcon from '@mui/icons-material/Home';
import ApartmentIcon from '@mui/icons-material/Apartment';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

import LineChart from "../../components/LineChart";
import BarChart from "../../components/BarChart";
import PaidIcon from "@mui/icons-material/Paid";
import { useNavigate } from "react-router-dom";
import { Colors } from "../../assets";
import ListAltIcon from "@mui/icons-material/ListAlt";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";

import { Loader, showError } from "../../constants";
import { downloadCsv } from "../../utils/exportCsv";
import {
  useGetDashboardCsvMutation,
  useLazyGetDashboardQuery,
  useLazyGetEarningGraphQuery,
  useLazyGetPropertiesSearchedByAreaQuery,
  useLazyGetUserGraphQuery,
} from "../../services/main";
import { Dashboard } from "../../types/General";
import useAuth from "../../hooks/useAuth";
import { Permissions } from "../../types/User";
import useTranslation from "../../hooks/Translation";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? Colors.primary : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const ShufflingNumber = ({
  loading,
  value,
  isCurrency = false,
}: {
  loading: boolean;
  value: number | string | undefined;
  isCurrency?: boolean;
}) => {
  const [displayValue, setDisplayValue] = useState<string>("0");

  useEffect(() => {
    if (loading) {
      const interval = setInterval(() => {
        const length = isCurrency ? 5 : 3;
        let randomNum = "";
        for (let i = 0; i < length; i++) {
          randomNum += Math.floor(Math.random() * 10).toString();
        }
        if (isCurrency) {
          setDisplayValue(
            `KD ${Number(randomNum).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
          );
        } else {
          setDisplayValue(Number(randomNum).toLocaleString());
        }
      }, 50);

      return () => clearInterval(interval);
    } else {
      if (value !== undefined && value !== null) {
        if (isCurrency) {
          setDisplayValue(
            `KD ${Number(value).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
          );
        } else {
          setDisplayValue(Number(value).toLocaleString());
        }
      } else {
        setDisplayValue(isCurrency ? "KD 0.00" : "0");
      }
    }
  }, [loading, value, isCurrency]);

  return <>{displayValue}</>;
};

const DashBoard = () => {
  const navigate = useNavigate();
  const userData = useAuth();
  const translate = useTranslation();
  const [searchProperty] = useLazyGetPropertiesSearchedByAreaQuery();
  const [getDashboardQuery] = useLazyGetDashboardQuery();
  const [searchData, setSearchData] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [hidePermission, setHidePermission] = useState<
    Permissions | null | undefined
  >(null);
  const [dashboardCsv] = useGetDashboardCsvMutation();
  const [loading, setLoading] = useState<boolean>(false);
  const [getUserGraphQuery] = useLazyGetUserGraphQuery();
  const [getEarningGraphQuery] = useLazyGetEarningGraphQuery();
  const [userFilter, setUserFilter] = useState<string>("monthly");
  const [earningFilter, setEarningFilter] = useState<string>("monthly");
  const [searchFilter, setSearchFilter] = useState<string>("monthly");
  const [dashboardData, setDashboardData] = useState<Dashboard>();
  const [userGraph, setUserGraph] = useState<any>([]);
  const [earningGraph, setEarningGraph] = useState<any>([]);

  const userPermissions = userData?.permission?.length
    ? userData?.permission
    : [];

  const handleOrderChange = (event: SelectChangeEvent) => {
    setUserFilter(event.target.value as string);
  };
  const handleSearchChange = (event: SelectChangeEvent) => {
    setSearchFilter(event.target.value as string);
  };

  const handleearningChange = (event: SelectChangeEvent) => {
    setEarningFilter(event.target.value as string);
  };

  function convertToInternationalCurrencySystem(labelValue: number) {
    // Nine Zeroes for Billions
    return Math.abs(Number(labelValue)) >= 1.0e9
      ? (Math.abs(Number(labelValue)) / 1.0e9).toFixed(2) + " B"
      : // Six Zeroes for Millions
      Math.abs(Number(labelValue)) >= 1.0e6
        ? (Math.abs(Number(labelValue)) / 1.0e6).toFixed(2) + " M"
        : // Three Zeroes for Thousands
        Math.abs(Number(labelValue)) >= 1.0e3
          ? (Math.abs(Number(labelValue)) / 1.0e3).toFixed(2) + " K"
          : Math.abs(Number(labelValue));
  }

  const getDashboard = async () => {
    setLoading(true);
    try {
      const response = await getDashboardQuery({}).unwrap();
      if (response?.statusCode === 200) {
        setLoading(false);
        setDashboardData(response?.data || "");
      } else {
        setDashboardData(undefined);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };
  const getPropertySearchedByArea = async () => {
    setSearchLoading(true);
    try {
      const response = await searchProperty({ type: searchFilter }).unwrap();
      if (response?.statusCode === 200) {
        const areaData = [...(response?.data?.userCount || [])]
          .sort((a: any, b: any) => (b?.count || 0) - (a?.count || 0))
          .slice(0, 10);
        setSearchData(areaData);
      } else {
        setSearchData([]);
      }
    } catch (error: any) {
      setSearchData([]);
      showError(error?.data?.message || "");
    } finally {
      setSearchLoading(false);
    }
  };

  const getDashboardCsv = async () => {
    try {
      const response = await dashboardCsv({}).unwrap();
      if (response?.statusCode === 200) {
        await downloadCsv(response?.data, "dashboard.csv");
      } else {
        showError(translate?.error?.exportFailed || "");
      }
    } catch (error: any) {
      showError(error?.data?.message || translate?.error?.exportFailed || "");
    }
  };

  const getEarningGraph = async () => {
    try {
      const response = await getEarningGraphQuery({
        type: earningFilter,
      }).unwrap();
      if (response?.statusCode === 200) {
        setEarningGraph(response?.data?.earningCount);
      } else {
        setEarningGraph([]);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const getUserGraph = async () => {
    try {
      const response = await getUserGraphQuery({
        type: userFilter,
      }).unwrap();
      if (response?.statusCode === 200) {
        setUserGraph(response?.data?.userCount);
      } else {
        setUserGraph([]);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const names = userGraph?.map((item: any) => item._id);
  const values = userGraph?.map((item: any) => item.count);

  const rNames = earningGraph?.map((item: any) => item._id);
  const rValues = earningGraph?.map((item: any) => item.count);

  const graphOrderData = {
    labels: names,
    datasets: [
      {
        fill: true,
        label: translate?.dashboard?.User,
        data: values,
        borderColor: Colors.primary,
        backgroundColor: "rgba(21, 29, 48, 0.1)",
      },
    ],
  };
  const graphRevenueData = {
    labels: rNames,
    datasets: [
      {
        fill: true,
        label: translate?.dashboard?.Earning,
        data: rValues,
        borderColor: Colors.primary,
        backgroundColor: "rgba(21, 29, 48, 0.1)",
      },
    ],
  };

  const sNames = searchData?.map((item: any) => item?.area || "-");
  const sData = searchData?.map((item: any) => item?.count || 0);

  const PropertyData = {
    labels: sNames?.length ? sNames : [translate?.error?.noData || "No data"],
    datasets: [
      {
        label: translate?.dashboard?.Property,
        data: sData?.length ? sData : [0],
        borderColor: Colors.primary,
        backgroundColor: "rgba(21, 29, 48, 0.75)",
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  };

  const refreshDashboard = () => {
    getDashboard();
    getUserGraph();
    getEarningGraph();
    getPropertySearchedByArea();
  };

  useEffect(() => {
    getDashboard();
  }, []);

  useEffect(() => {
    getUserGraph();
  }, [userFilter]);

  useEffect(() => {
    getPropertySearchedByArea();
  }, [searchFilter]);

  useEffect(() => {
    getEarningGraph();
  }, [earningFilter]);

  const checkPermission = () => {
    const permission = userPermissions;

    if (permission?.length) {
      let idx = -1;
      idx = permission?.findIndex(
        (ele: Permissions) => ele?.name === "Dashboard"
      );

      if (idx > -1) {
        setHidePermission(permission[idx]);
      } else {
        navigate("/permission-denied", { replace: true });
      }
    }
  };

  useEffect(() => {
    if (userData && userPermissions?.length) {
      checkPermission();
    }
  }, [userData]);

  return (
    <div className="main_loyout">
      <div className="dashboard">
        <h1 className="mn_hdng">{translate?.dashboard?.dashboard}</h1>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <Button className="btn btn_primary" onClick={refreshDashboard}>
            <RefreshIcon /> {translate?.dashboard?.refresh}
          </Button>
          <Button className="btn btn_primary" onClick={getDashboardCsv}>
            <FileDownloadIcon /> {translate?.dashboard?.ExportCsv}
          </Button>
        </div>
      </div>

      <Grid container spacing={3} className="dashGrid">
        <Grid item lg={3} md={3} sm={6} xs={12}>
          <Item
            className="cards dashGrid_item"
            onClick={() => navigate("/manageusers")}
          >
            <div className="card_top">
              <h3>{translate?.dashboard?.totalUsers} </h3>
              <div className="icon_wrapper" style={{ backgroundColor: "rgba(33, 150, 243, 0.08)" }}>
                <PeopleIcon className="svg_icn" style={{ color: "#2196f3" }} />
              </div>
            </div>
            <div className="card_bottom">
              <h4 className="mn_hdng">
                <ShufflingNumber loading={loading} value={dashboardData?.totalUser} />
              </h4>
            </div>
          </Item>
        </Grid>

        <Grid item lg={3} md={3} sm={6} xs={12}>
          <Item
            className="cards dashGrid_item"
            onClick={() => navigate("/manage-agents")}
          >
            <div className="card_top">
              <h3>{translate?.dashboard?.totalAgent} </h3>
              <div className="icon_wrapper" style={{ backgroundColor: "rgba(156, 39, 176, 0.08)" }}>
                <SupportAgentIcon className="svg_icn" style={{ color: "#9c27b0" }} />
              </div>
            </div>
            <div className="card_bottom">
              <h4 className="mn_hdng">
                <ShufflingNumber loading={loading} value={dashboardData?.totalAgent} />
              </h4>
            </div>
          </Item>
        </Grid>

        <Grid item lg={3} md={3} sm={6} xs={12}>
          <Item
            className="cards dashGrid_item"
          >
            <div className="card_top">
              <h3>{translate?.dashboard?.totalProperty} </h3>
              <div className="icon_wrapper" style={{ backgroundColor: "rgba(233, 30, 99, 0.08)" }}>
                <MapsHomeWorkIcon className="svg_icn" style={{ color: "#e91e63" }} />
              </div>
            </div>
            <div className="card_bottom">
              <h4 className="mn_hdng">
                <ShufflingNumber loading={loading} value={dashboardData?.totalProperties} />
              </h4>
            </div>
          </Item>
        </Grid>
        <Grid item lg={3} md={3} sm={6} xs={12}>
          <Item
            className="cards dashGrid_item"
          >
            <div className="card_top">
              <h3>{translate?.dashboard?.forSale} </h3>
              <div className="icon_wrapper" style={{ backgroundColor: "rgba(76, 175, 80, 0.08)" }}>
                <HomeIcon className="svg_icn" style={{ color: "#4caf50" }} />
              </div>
            </div>
            <div className="card_bottom">
              <h4 className="mn_hdng">
                <ShufflingNumber loading={loading} value={dashboardData?.propertiesForSale} />
              </h4>
            </div>
          </Item>
        </Grid>
        <Grid item lg={3} md={3} sm={6} xs={12}>
          <Item
            className="cards dashGrid_item"
          >
            <div className="card_top">
              <h3>{translate?.dashboard?.forRent} </h3>
              <div className="icon_wrapper" style={{ backgroundColor: "rgba(255, 152, 0, 0.08)" }}>
                <ApartmentIcon className="svg_icn" style={{ color: "#ff9800" }} />
              </div>
            </div>
            <div className="card_bottom">
              <h4 className="mn_hdng">
                <ShufflingNumber loading={loading} value={dashboardData?.propertiesForRent} />
              </h4>
            </div>
          </Item>
        </Grid>
        <Grid item lg={3} md={3} sm={6} xs={12}>
          <Item
            className="cards dashGrid_item"
          >
            <div className="card_top">
              <h3>{translate?.dashboard?.forExchange}</h3>
              <div className="icon_wrapper" style={{ backgroundColor: "rgba(0, 150, 136, 0.08)" }}>
                <SwapHorizIcon className="svg_icn" style={{ color: "#009688" }} />
              </div>
            </div>
            <div className="card_bottom">
              <h4 className="mn_hdng">
                <ShufflingNumber loading={loading} value={dashboardData?.propertiesForExchange} />
              </h4>
            </div>
          </Item>
        </Grid>
        <Grid item lg={3} md={3} sm={6} xs={12}>
          <Item
            className="cards dashGrid_item"
          >
            <div className="card_top">
              <h3>{translate?.dashboard?.earnings}</h3>
              <div className="icon_wrapper" style={{ backgroundColor: "rgba(63, 81, 181, 0.08)" }}>
                <PaidIcon className="svg_icn" style={{ color: "#3f51b5" }} />
              </div>
            </div>
            <div className="card_bottom">
              <h4 className="mn_hdng">
                <ShufflingNumber loading={loading} value={dashboardData?.totalEarning} isCurrency />
              </h4>
            </div>
          </Item>
        </Grid>
        <Grid item lg={3} md={3} sm={6} xs={12}>
          <Item
            className="cards dashGrid_item"
          >
            <div className="card_top">
              <h3>{translate?.dashboard?.avgEarnings} </h3>
              <div className="icon_wrapper" style={{ backgroundColor: "rgba(63, 81, 181, 0.08)" }}>
                <CurrencyExchangeIcon className="svg_icn" style={{ color: "#3f51b5" }} />
              </div>
            </div>
            <div className="card_bottom">
              <h4 className="mn_hdng">
                <ShufflingNumber loading={loading} value={dashboardData?.avgMonthlyEarning} isCurrency />
              </h4>
            </div>
          </Item>
        </Grid>
        <Grid item lg={3} md={3} sm={6} xs={12}>
          <Item className="cards dashGrid_item">
            <div className="card_top">
              <h3>{translate?.dashboard?.userEarning}</h3>
              <div className="icon_wrapper" style={{ backgroundColor: "rgba(0, 188, 212, 0.08)" }}>
                <AccountBalanceWalletIcon className="svg_icn" style={{ color: "#00bcd4" }} />
              </div>
            </div>
            <div className="card_bottom">
              <h4 className="mn_hdng">
                <ShufflingNumber loading={loading} value={dashboardData?.userEarning} isCurrency />
              </h4>
            </div>
          </Item>
        </Grid>
        <Grid item lg={3} md={3} sm={6} xs={12}>
          <Item className="cards dashGrid_item">
            <div className="card_top">
              <h3>{translate?.dashboard?.agentEarning}</h3>
              <div className="icon_wrapper" style={{ backgroundColor: "rgba(0, 188, 212, 0.08)" }}>
                <AccountBalanceWalletIcon className="svg_icn" style={{ color: "#00bcd4" }} />
              </div>
            </div>
            <div className="card_bottom">
              <h4 className="mn_hdng">
                <ShufflingNumber loading={loading} value={dashboardData?.agentEarning} isCurrency />
              </h4>
            </div>
          </Item>
        </Grid>
        <Grid item lg={3} md={3} sm={6} xs={12}>
          <Item className="cards dashGrid_item">
            <div className="card_top">
              <h3>{translate?.dashboard?.subscriptionEarning}</h3>
              <div className="icon_wrapper" style={{ backgroundColor: "rgba(139, 195, 74, 0.08)" }}>
                <PaidIcon className="svg_icn" style={{ color: "#8bc34a" }} />
              </div>
            </div>
            <div className="card_bottom">
              <h4 className="mn_hdng">
                <ShufflingNumber loading={loading} value={dashboardData?.subscriptionEarning} isCurrency />
              </h4>
            </div>
          </Item>
        </Grid>
      </Grid>

      <Grid container spacing={3} className="dashGraph" sx={{ pt: 4, alignItems: "stretch" }}>
        <Grid item xs={12}>
          <Item className="cards dashGraph_item">
            <h2 className="mn_hdng">
              {translate?.dashboard?.earnings}
              <FormControl>
                <Select
                  className="select_div"
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={earningFilter}
                  inputProps={{
                    "aria-label": "Without label",
                  }}
                  displayEmpty
                  onChange={handleearningChange}
                >
                  <MenuItem value="" disabled>
                    {translate?.dashboard?.Select}
                  </MenuItem>
                  <MenuItem value="hourly">
                    {" "}
                    {translate?.dashboard?.Hourly}
                  </MenuItem>
                  <MenuItem value="weekly">
                    {" "}
                    {translate?.dashboard?.Weekly}
                  </MenuItem>
                  <MenuItem value="monthly">
                    {" "}
                    {translate?.dashboard?.Monthly}
                  </MenuItem>
                  <MenuItem value="yearly">
                    {" "}
                    {translate?.dashboard?.Yearly}
                  </MenuItem>
                </Select>
              </FormControl>
            </h2>
            {loading ? (
              <Box sx={{ py: 1 }}>
                <Skeleton variant="rectangular" height={280} sx={{ borderRadius: "12px", width: "100%" }} animation="wave" />
              </Box>
            ) : (
              <LineChart data={graphRevenueData} height={280} />
            )}
          </Item>
        </Grid>
        <Grid item lg={6} md={6} sm={12} xs={12} sx={{ display: "flex" }}>
          <Item className="cards dashGraph_item" sx={{ flex: 1, width: "100%" }}>
            <h2 className="mn_hdng">
              {translate?.dashboard?.totalUsers}
              <FormControl>
                <Select
                  className="select_div"
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={userFilter}
                  inputProps={{
                    "aria-label": "Without label",
                  }}
                  displayEmpty
                  onChange={handleOrderChange}
                >
                  <MenuItem value="" disabled>
                    {translate?.dashboard?.Select}
                  </MenuItem>
                  <MenuItem value="hourly">
                    {" "}
                    {translate?.dashboard?.Hourly}
                  </MenuItem>
                  <MenuItem value="weekly">
                    {" "}
                    {translate?.dashboard?.Weekly}
                  </MenuItem>
                  <MenuItem value="monthly">
                    {" "}
                    {translate?.dashboard?.Monthly}
                  </MenuItem>
                  <MenuItem value="yearly">
                    {" "}
                    {translate?.dashboard?.Yearly}
                  </MenuItem>
                </Select>
              </FormControl>
            </h2>
            {loading ? (
              <Box sx={{ py: 1 }}>
                <Skeleton variant="rectangular" height={280} sx={{ borderRadius: "12px", width: "100%" }} animation="wave" />
              </Box>
            ) : (
              <LineChart data={graphOrderData} height={280} />
            )}
          </Item>
        </Grid>

        <Grid item lg={6} md={6} sm={12} xs={12} sx={{ display: "flex" }}>
          <Item className="cards dashGraph_item" sx={{ flex: 1, width: "100%" }}>
            <h2 className="mn_hdng">
              {translate?.dashboard?.byArea}
              <FormControl>
                <Select
                  className="select_div"
                  labelId="area-search-filter-label"
                  id="area-search-filter"
                  value={searchFilter}
                  inputProps={{
                    "aria-label": "Without label",
                  }}
                  displayEmpty
                  onChange={handleSearchChange}
                >
                  <MenuItem value="" disabled>
                    {translate?.dashboard?.Select}
                  </MenuItem>
                  <MenuItem value="hourly">
                    {translate?.dashboard?.Hourly}
                  </MenuItem>
                  <MenuItem value="weekly">
                    {translate?.dashboard?.Weekly}
                  </MenuItem>
                  <MenuItem value="monthly">
                    {translate?.dashboard?.Monthly}
                  </MenuItem>
                  <MenuItem value="yearly">
                    {translate?.dashboard?.Yearly}
                  </MenuItem>
                </Select>
              </FormControl>
            </h2>
            {searchLoading ? (
              <Box sx={{ py: 1 }}>
                <Skeleton variant="rectangular" height={280} sx={{ borderRadius: "12px", width: "100%" }} animation="wave" />
              </Box>
            ) : (
              <BarChart key={searchFilter} data={PropertyData} height={280} />
            )}
          </Item>
        </Grid>
      </Grid>
    </div>
  );
};

export default DashBoard;
